import { UpdateSopReadingsRequest } from "@features/wx/master/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  BarChart3,
  Camera,
  CheckCircle2,
  ChevronRight,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useSopReadingOptions, useUpdateSopReadings } from "../../../hooks";

// Schema for readings validation
const readingsSchema = z
  .object({
    // Dynamic reading fields will be added at runtime
  })
  .catchall(z.any()); // Allow dynamic fields

interface SopReadingsStepProps {
  state: any;
  onUpdateState: (updates: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
  templateId: string;
  onStepSave?: (stepIndex: number, data: any, isDirty: boolean) => void;
}

const SopReadingsStep: React.FC<SopReadingsStepProps> = ({
  state,
  onUpdateState,
  onNext,
  onPrevious,
  readOnly = false,
  templateId,
  onStepSave,
}) => {
  // Get operation type from basic info step
  const operationType = state.basicInfo?.operationType || "";
  // Get SOP ID from basic info step (created in previous step)
  const sopId = state.basicInfo?.sopId || templateId;
  const utilityId = useRemoteUtilityId();
  const { toast } = useToast();
  const [isManualAdd, setIsManualAdd] = useState(false);
  const [isManualDelete, setIsManualDelete] = useState(false);

  const [isFormInitialized, setIsFormInitialized] = useState(false);

  // Add the update SOP readings hook
  const updateSopReadingsMutation = useUpdateSopReadings();

  // Fetch API readings based on operation type
  const {
    options: apiReadings,
    isLoading: loadingApiReadings,
    error: apiReadingsError,
  } = useSopReadingOptions(operationType);

  // ✅ FIXED: Create default values for form with proper handling
  // ✅ FIXED: Update createDefaultValues to handle merged readings
  const createDefaultValues = useCallback(() => {
    const defaultValues: any = {};

    // Merge readings from both sources - same as in allFormFields
    const readings = [
      ...(state.readings || []),
      ...(state.basicInfo?.unitReadings || []),
    ];

    readings.forEach((reading: any, index: number) => {
      defaultValues[`reading_${index}_name`] = reading.name || "";
      defaultValues[`reading_${index}_unit`] = reading.unit || "";
      defaultValues[`reading_${index}_min`] = reading.minimum || "";
      defaultValues[`reading_${index}_max`] = reading.maximum || "";
      defaultValues[`reading_${index}_criticality`] =
        reading.criticality || "medium";
      defaultValues[`reading_${index}_photoRequired`] =
        reading.photoRequired === "Yes" || reading.photoRequired === true;
    });

    return defaultValues;
  }, [state.readings, state.basicInfo?.unitReadings]); // ✅ Add unitReadings to dependencies
  const readingsForm = useForm<z.infer<typeof readingsSchema>>({
    resolver: zodResolver(readingsSchema),
    defaultValues: createDefaultValues(),
  });

  // ✅ FIXED: Calculate total readings and photo count using both state and form values
  const { totalReadings, photoRequiredCount, criticalReadings } =
    useMemo(() => {
      const readings = state.readings || [];
      const formValues = readingsForm.getValues();

      let photoRequiredCount = 0;
      let criticalReadings = 0;

      readings.forEach((reading: any, index: number) => {
        // Get photo requirement from form first, then fallback to state
        const photoRequired =
          formValues[`reading_${index}_photoRequired`] !== undefined
            ? formValues[`reading_${index}_photoRequired`]
            : reading.photoRequired || false;

        // Get criticality from form first, then fallback to state
        const criticality =
          formValues[`reading_${index}_criticality`] !== undefined
            ? formValues[`reading_${index}_criticality`]
            : reading.criticality || "medium";

        if (photoRequired) photoRequiredCount++;
        if (criticality === "high") criticalReadings++;
      });

      return {
        totalReadings: readings.length,
        photoRequiredCount,
        criticalReadings,
      };
    }, [state.readings, readingsForm.watch()]); // ✅ Watch form changes

  // Track if API readings have been loaded for this operation type
  const [lastLoadedOperationType, setLastLoadedOperationType] = useState("");
  const [apiReadingsLoaded, setApiReadingsLoaded] = useState(false);

  // ✅ Enhanced Transform readings data to API format
  const transformReadingsToApiFormat = useCallback(
    (readings: any[]): UpdateSopReadingsRequest => {
      // Filter out empty readings and transform to API format
      const transformedReadings = readings
        .filter((reading) => {
          // Only include readings that have at least a name
          return reading.name && reading.name.trim() !== "";
        })
        .map((reading) => {
          const transformedReading = {
            name: reading.name || "",
            unit: reading.unit || "",
            minimum: reading.minimum || "0",
            maximum: reading.maximum || "0",
            isPhotoReq: reading.photoRequired ? "Yes" : "No",
            key: reading.key,
            code: reading.code,
          };

          return transformedReading;
        });

      const apiData = {
        readings: transformedReadings,
        remoteUtilityId: parseInt(utilityId || "0"),
        readingsDataUpdated: true,
      };

      return apiData;
    },
    [utilityId]
  );

  // Effect to load API readings when operation type changes
  useEffect(() => {
    if (
      operationType &&
      operationType !== lastLoadedOperationType &&
      apiReadings &&
      apiReadings.length > 0 &&
      !loadingApiReadings
    ) {
      setLastLoadedOperationType(operationType);
      setApiReadingsLoaded(true);
    }
  }, [operationType, apiReadings, loadingApiReadings, lastLoadedOperationType]);

  // Initialize readings array if it doesn't exist
  useEffect(() => {
    if (!state.readings) {
      onUpdateState({
        readings: [],
      });
    }
  }, [state.readings, onUpdateState]);

  // ✅ FIXED: Improved form initialization and population
  useEffect(() => {
    if (state.readings) {
      if (isManualAdd || isManualDelete) {
        setIsManualAdd(false); // Reset the flags
        setIsManualDelete(false);
        return; // Don't reset the form
      }
      const defaultValues = createDefaultValues();

      // Always reset form with current state values
      readingsForm.reset(defaultValues);
      setIsFormInitialized(true);
    }
  }, [state.readings, createDefaultValues, readingsForm]);

  // ✅ FIXED: Function to handle individual reading card selection
  const handleReadingSelect = useCallback(
    (apiReading: any) => {
      // Create a new reading based on the selected card
      const newReading = {
        id: Date.now(),
        name: apiReading.label || "",
        unit: apiReading.unit || "",
        minimum: apiReading.minimum || "",
        maximum: apiReading.maximum || "",
        criticality: "medium",
        photoRequired:
          apiReading.photoRequired === "Yes" ||
          apiReading.photoRequired === true,
        isFromApi: true,
        apiCode: apiReading.value,
      };

      // Add the reading to existing state
      const currentReadings = state.readings || [];
      const updatedReadings = [...currentReadings, newReading];

      // Update state first
      onUpdateState({
        readings: updatedReadings,
      });

      // ✅ FIXED: Immediate form value setting without setTimeout
      const readingIndex = currentReadings.length;
      const readingFieldValues = {
        [`reading_${readingIndex}_name`]: newReading.name,
        [`reading_${readingIndex}_unit`]: newReading.unit,
        [`reading_${readingIndex}_min`]: newReading.minimum,
        [`reading_${readingIndex}_max`]: newReading.maximum,
        [`reading_${readingIndex}_criticality`]: newReading.criticality,
        [`reading_${readingIndex}_photoRequired`]: newReading.photoRequired,
      };

      // Set all form values at once
      Object.entries(readingFieldValues).forEach(([key, value]) => {
        readingsForm.setValue(key, value, {
          shouldDirty: true,
          shouldValidate: true,
        });
      });

      toast({
        title: "Reading Added",
        description: `${newReading.name} has been added to your readings.`,
        duration: 2000,
      });
    },
    [state.readings, onUpdateState, readingsForm, toast]
  );

  // Get reading type based on unit or name
  const getReadingType = (reading: any) => {
    const name = reading.label?.toLowerCase() || "";
    const unit = reading.unit?.toLowerCase() || "";

    if (
      name.includes("voltage") ||
      name.includes("current") ||
      name.includes("electrical") ||
      unit.includes("v") ||
      unit.includes("a")
    ) {
      return { type: "electrical", color: "text-yellow-600 bg-yellow-100" };
    } else if (
      name.includes("pressure") ||
      unit.includes("psi") ||
      unit.includes("kpa")
    ) {
      return { type: "pressure", color: "text-red-600 bg-red-100" };
    } else if (
      name.includes("flow") ||
      name.includes("rate") ||
      unit.includes("gpm") ||
      unit.includes("lpm")
    ) {
      return { type: "flow", color: "text-blue-600 bg-blue-100" };
    } else if (
      name.includes("temperature") ||
      unit.includes("°c") ||
      unit.includes("°f")
    ) {
      return { type: "temperature", color: "text-orange-600 bg-orange-100" };
    } else if (
      name.includes("quality") ||
      name.includes("ph") ||
      name.includes("chlorine")
    ) {
      return { type: "quality", color: "text-green-600 bg-green-100" };
    } else {
      return { type: "standard", color: "text-gray-600 bg-gray-100" };
    }
  };

  // Render individual reading cards
  const renderReadingCards = () => {
    if (loadingApiReadings) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-lg p-6 animate-pulse"
            >
              <div className="h-6 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      );
    }

    if (apiReadingsError) {
      return (
        <div className="p-6 border border-red-200 rounded-lg bg-red-50">
          <p className="text-red-800">
            Error loading reading templates. Please try again.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            className="mt-2"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      );
    }

    if (!apiReadings || apiReadings.length === 0) {
      return (
        <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
          <p className="text-gray-600">
            No reading templates available for the selected operation type.
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {apiReadings.map((reading) => {
          const readingType = getReadingType(reading);
          return (
            <div
              key={reading.value}
              className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer bg-white"
              onClick={() => handleReadingSelect(reading)}
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-semibold text-lg text-gray-900">
                  {reading.label}
                </h3>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Unit:</span>
                  <span className="font-medium text-gray-900">
                    {reading.unit}
                  </span>
                </div>

                {(reading.minimum || reading.maximum) && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Range:</span>
                    <span className="font-medium text-gray-900">
                      {reading.minimum} - {reading.maximum}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-2">
                  {reading.photoRequired && (
                    <div className="flex items-center gap-1 text-xs text-blue-600">
                      <Camera className="h-3 w-3" />
                      <span>Photo Required</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${readingType.color}`}
                >
                  {readingType.type}
                </span>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ✅ Enhanced Form submit with proper form data capture
  const handleFormSubmit = useCallback(
    async (data: any) => {
      // Check if we have a SOP ID
      if (!sopId) {
        toast({
          title: "Error",
          description:
            "SOP ID is missing. Please complete the Basic Information step first.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      if (!utilityId) {
        toast({
          title: "Error",
          description: "Utility ID is missing.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      try {
        // ✅ Get form values and merge with state readings
        const formValues = readingsForm.getValues();
        const baseReadings = (state.readings || []).map((item: any) => ({
          ...item,
        }));
        const unitReadings = state.basicInfo?.unitReadings || [];

        // list of names from base
        const existingNames = new Set(
          baseReadings.map((item: any) => item.name?.toLowerCase())
        );

        // If name exists in base, mark base item as isFromUnit: true
        baseReadings.forEach((item: any) => {
          const nameKey = item.name?.toLowerCase();

          if (
            unitReadings.some((u: any) => u.name?.toLowerCase() === nameKey)
          ) {
            item.isFromUnit = true;
          } else {
            item.isFromUnit = false;
          }
        });

        // include only those unit readings whose name doesn't exist in base
        const filteredUnitReadings = unitReadings
          .filter((item: any) => !existingNames.has(item.name?.toLowerCase()))
          .map((item: any) => ({
            ...item,
            isFromUnit: true,
          }));

        const currentReadings = [...baseReadings, ...filteredUnitReadings];
        // ✅ Update readings with current form values
        const updatedReadings = currentReadings.map(
          (reading: any, index: number) => ({
            ...reading,
            name: formValues[`reading_${index}_name`] || reading.name || "",
            unit: formValues[`reading_${index}_unit`] || reading.unit || "",
            minimum:
              formValues[`reading_${index}_min`] || reading.minimum || "",
            maximum:
              formValues[`reading_${index}_max`] || reading.maximum || "",
            criticality:
              formValues[`reading_${index}_criticality`] ||
              reading.criticality ||
              "medium",
            photoRequired:
              formValues[`reading_${index}_photoRequired`] !== undefined
                ? formValues[`reading_${index}_photoRequired`]
                : reading.photoRequired === "Yes" ||
                  reading.photoRequired === true,
          })
        );

        if (updatedReadings.length === 0) {
          toast({
            title: "No Readings",
            description: "Please add at least one reading before proceeding.",
            variant: "destructive",
            duration: 5000,
          });
          return;
        }

        // ✅ Check if we have at least one reading with a name
        const validReadings = updatedReadings.filter(
          (reading: any) => reading.name && reading.name.trim() !== ""
        );

        if (validReadings.length === 0) {
          toast({
            title: "Invalid Readings",
            description: "Please ensure at least one reading has a name.",
            variant: "destructive",
            duration: 5000,
          });
          return;
        }

        // Transform readings to API format
        const apiData = transformReadingsToApiFormat(updatedReadings);

        // Call the API to update SOP with readings
        const response = await updateSopReadingsMutation.mutateAsync({
          sopId: sopId,
          data: apiData,
        });
        if (onStepSave) {
          await onStepSave(
            4,
            {
              // step index 4 for readings
              readings: updatedReadings,
            },
            true
          );
        }
        // Show success toast
        toast({
          title: "Readings Updated Successfully",
          description: `${validReadings.length} readings have been added to the SOP.`,
          duration: 3000,
        });

        // Update local state with the updated readings
        onUpdateState({
          readings: updatedReadings,
        });

        // Proceed to next step
        if (onNext) {
          onNext();
        }
      } catch (error: any) {
        toast({
          title: "Failed to Update Readings",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "An error occurred while updating readings. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      }
    },
    [
      sopId,
      utilityId,
      state.readings,
      readingsForm,
      transformReadingsToApiFormat,
      updateSopReadingsMutation,
      onUpdateState,
      onNext,
      toast,
      onStepSave,
    ]
  );

  // Define form fields dynamically
  const allFormFields: FormField[] = useMemo(() => {
    const fields: FormField[] = [];

    const baseReadings = (state.readings || []).map((item: any) => ({
      ...item,
    }));
    const unitReadings = state.basicInfo?.unitReadings || [];

    // list of names from base
    const existingNames = new Set(
      baseReadings.map((item: any) => item.name?.toLowerCase())
    );

    // If name exists in base, mark base item as isFromUnit: true
    baseReadings.forEach((item: any) => {
      const nameKey = item.name?.toLowerCase();

      if (unitReadings.some((u: any) => u.name?.toLowerCase() === nameKey)) {
        item.isFromUnit = true;
      } else {
        item.isFromUnit = false;
      }
    });

    // include only those unit readings whose name doesn't exist in base
    const filteredUnitReadings = unitReadings
      .filter((item: any) => !existingNames.has(item.name?.toLowerCase()))
      .map((item: any) => ({
        ...item,
        isFromUnit: true,
      }));

    const readings = [...baseReadings, ...filteredUnitReadings];

    if (readings.length > 0) {
      readings.forEach((reading: any, index: number) => {
        // Reading container with border
        fields.push({
          name: `reading_${index}_container`,
          label: "",
          type: "text",
          disabled: true,
          value: "",
          customRender: () => (
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-lg text-gray-900">
                  Reading {index + 1}
                </h4>
                {reading.isFromApi && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    Template
                  </span>
                )}
                {reading.isDefault && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                    Default
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeReading(index)}
                disabled={readOnly || reading.isFromUnit}
                type="button"
                className="text-gray-400 hover:text-red-500"
              >
                <X size={18} />
              </Button>
            </div>
          ),
          className: "col-span-6",
          classes: {
            container: `w-full bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm ${
              reading.isFromApi
                ? "border-blue-200"
                : reading.isDefault
                ? "border-green-200"
                : ""
            }`,
          },
          fullWidth: true,
        });

        // Reading fields in 2x2 layout
        fields.push(
          {
            name: `reading_${index}_name`,
            label: "Reading Name",
            type: "text",
            required: true,
            placeholder: "e.g. Water Pressure, Voltage Reading",
            value: reading.name || "",
            disabled:
              readOnly ||
              (reading.isFromApi && !reading.allowEdit) ||
              reading.isFromUnit,
            onChange: (value: string) => updateReading(index, "name", value),
            className: "col-span-3",
            classes: {
              container: "w-full",
              label: "block text-sm font-medium text-gray-900 mb-2",
              input: `w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 ${
                reading.isFromApi ? "bg-blue-50" : ""
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          },
          {
            name: `reading_${index}_unit`,
            label: "Unit of Measurement",
            type: "text",
            required: true,
            placeholder: "e.g. PSI, kWh, °C, pH",
            value: reading.unit || "",
            disabled: readOnly || reading.isFromUnit,
            onChange: (value: string) => updateReading(index, "unit", value),
            className: "col-span-3",
            classes: {
              container: "w-full",
              label: "block text-sm font-medium text-gray-900 mb-2",
              input: `w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 ${
                reading.isFromApi ? "bg-blue-50" : ""
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          },
          {
            name: `reading_${index}_min`,
            label: "Minimum Value",
            type: "number",
            placeholder: "Min acceptable value",
            value: reading.minimum || "",
            disabled: readOnly || reading.isFromUnit,
            onChange: (value: string) => updateReading(index, "minimum", value),
            className: "col-span-3",
            classes: {
              container: "w-full",
              label: "block text-sm font-medium text-gray-900 mb-2",
              input: `w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 ${
                reading.isFromApi ? "bg-blue-50" : ""
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          },
          {
            name: `reading_${index}_max`,
            label: "Maximum Value",
            type: "number",
            placeholder: "Max acceptable value",
            value: reading.maximum || "",
            disabled: readOnly || reading.isFromUnit,
            onChange: (value: string) => updateReading(index, "maximum", value),
            className: "col-span-3",
            classes: {
              container: "w-full",
              label: "block text-sm font-medium text-gray-900 mb-2",
              input: `w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 ${
                reading.isFromApi ? "bg-blue-50" : ""
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          }
        );

        // Criticality and Photo Required
        fields.push(
          {
            name: `reading_${index}_criticality`,
            label: "Criticality Level",
            type: "select",
            options: [
              { label: "Low", value: "low" },
              { label: "Medium", value: "medium" },
              { label: "High", value: "high" },
            ],
            value: reading.criticality || "medium",
            disabled: readOnly || reading.isFromUnit,
            onChange: (value: string) =>
              updateReading(index, "criticality", value),
            className: "col-span-3",
            classes: {
              container: "w-full",
              label: "block text-sm font-medium text-gray-900 mb-2",
              select: `w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors cursor-pointer bg-white text-gray-900 ${
                reading.isFromApi ? "bg-blue-50" : ""
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          },
          {
            name: `reading_${index}_photoRequired`,
            label: "Photo Required",
            type: "checkbox",
            value:
              reading.photoRequired === "Yes" || reading.photoRequired === true,
            disabled: readOnly || reading.isFromUnit,
            icon: <Camera size={16} className="ml-1 text-gray-500" />,
            onChange: (value: boolean) =>
              updateReading(index, "photoRequired", value),
            className: "col-span-3",
            classes: {
              container: "w-full flex items-center pt-8",
              label: "text-sm font-medium flex items-center",
            },
          }
        );
      });
    }

    return fields;
  }, [state.readings, readOnly]);

  // ✅ FIXED: Add reading with proper form handling
  const addReading = useCallback(() => {
    const currentReadings = [
      ...(state.readings || []),
      ...(state.basicInfo?.unitReadings?.map((item: any) => ({
        ...item,
        isFromUnit: true,
      })) || []),
    ];
    const newReading = {
      id:
        currentReadings.length > 0
          ? Math.max(...currentReadings.map((r: any) => r.id || 0)) + 1
          : 1,
      name: "",
      unit: "",
      minimum: "",
      maximum: "",
      criticality: "medium",
      photoRequired: false,
      isFromApi: false,
    };
    setIsManualAdd(true);

    const updatedReadings = [...currentReadings, newReading];
    onUpdateState({
      readings: updatedReadings,
    });

    // ✅ Set form values immediately
    const newIndex = updatedReadings.length - 1;
    const readingFieldValues = {
      [`reading_${newIndex}_name`]: "",
      [`reading_${newIndex}_unit`]: "",
      [`reading_${newIndex}_min`]: "",
      [`reading_${newIndex}_max`]: "",
      [`reading_${newIndex}_criticality`]: "medium",
      [`reading_${newIndex}_photoRequired`]: false,
    };

    Object.entries(readingFieldValues).forEach(([key, value]) => {
      readingsForm.setValue(key, value, { shouldDirty: true });
    });
  }, [state.readings, onUpdateState, readingsForm]);

  const removeReading = useCallback(
    (index: number) => {
      const currentReadings = state.readings || [];
      const updatedReadings = [...currentReadings];
      updatedReadings.splice(index, 1);
      setIsManualDelete(true);
      onUpdateState({
        readings: updatedReadings,
      });
    },
    [state.readings, onUpdateState]
  );

  const updateReading = useCallback(
    (index: number, field: string, value: any) => {
      const currentReadings = state.readings || [];
      const updatedReadings = currentReadings.map((reading: any, i: number) =>
        i === index ? { ...reading, [field]: value } : reading
      );

      onUpdateState({
        readings: updatedReadings,
      });

      // Also update the form value immediately to prevent clearing
      readingsForm.setValue(`reading_${index}_${field}`, value, {
        shouldDirty: true,
      });
    },
    [state.readings, onUpdateState, readingsForm]
  );

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Handle field changes if needed
  }, []);

  const renderCustomFooter = useCallback(() => {
    if (readOnly) return null;

    const isSubmitting = updateSopReadingsMutation.isPending;

    return (
      <div className="space-y-4">
        {/* Show warning if no SOP ID */}
        {!sopId && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Warning:</strong> No SOP ID found. Please complete the
              Basic Information step first.
            </p>
          </div>
        )}

        {/* Add Reading Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={addReading}
          type="button"
          disabled={isSubmitting}
        >
          <Plus size={16} className="mr-2" /> Add Manual Reading
        </Button>

        {/* Navigation */}
        <div className="pt-6 flex justify-between border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onPrevious}
            type="button"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !sopId}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating Readings...
              </>
            ) : (
              <>
                Next <ChevronRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }, [
    readOnly,
    onPrevious,
    addReading,
    updateSopReadingsMutation.isPending,
    sopId,
  ]);

  // ✅ Debug logging for troubleshooting
  useEffect(() => {
    console.log("🔍 Readings Debug Info:", {
      readingsCount: state.readings?.length || 0,
      isFormInitialized,
      formValues: readingsForm.getValues(),
    });
  }, [state.readings, isFormInitialized, readingsForm]);

  return (
    <div className="space-y-6">
      {/* Show loading overlay during API call */}
      {updateSopReadingsMutation.isPending && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-lg font-medium">
                Updating SOP Readings...
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Show message if no operation type selected */}
      {!operationType && (
        <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
          <p className="text-yellow-800">
            Please select an Operation Type in the Basic Information step to
            load available readings.
          </p>
        </div>
      )}

      {/* Main Content */}
      {/* Always show reading template cards at the top */}
      <div>
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Reading Templates
              </h2>
              <p className="text-gray-600">
                Select from pre-defined reading templates or create your own
              </p>
            </div>

            {/* ✅ Total Readings and Summary Display */}
            {state.readings && state.readings.length > 0 && (
              <div className="flex gap-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                  <div className="text-sm">
                    <span className="font-medium text-blue-900">
                      Total Readings:
                    </span>
                    <span className="ml-1 text-blue-700">{totalReadings}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                  <Camera className="h-4 w-4 text-purple-600" />
                  <div className="text-sm">
                    <span className="font-medium text-purple-900">
                      Photos Required:
                    </span>
                    <span className="ml-1 text-purple-700">
                      {photoRequiredCount}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <CheckCircle2 className="h-4 w-4 text-red-600" />
                  <div className="text-sm">
                    <span className="font-medium text-red-900">Critical:</span>
                    <span className="ml-1 text-red-700">
                      {criticalReadings}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {renderReadingCards()}
      </div>

      {/* Always show the DynamicForm and footer, even if there are no readings */}
      <div className="mt-8">
        {state.readings && state.readings.length > 0 && (
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Readings</h2>
        )}
        {isFormInitialized && (
          <DynamicForm
            fields={allFormFields}
            form={readingsForm}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
            config={{
              showSubmitButton: false,
              showResetButton: false,
              classes: {
                form: "space-y-8",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500",
                textarea:
                  "w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500 resize-vertical",
                select:
                  "w-full h-11 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors cursor-pointer bg-white text-gray-900",
                label: "block text-sm font-medium text-gray-900 mb-2",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-2",
              },
            }}
            gridColumns={6}
            responsiveColumns={{ sm: 1, md: 1 }}
            renderCustomFooter={renderCustomFooter}
            className="space-y-8"
          />
        )}
      </div>
    </div>
  );
};

export default SopReadingsStep;
